import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Category, MenuItem, Restaurant } from '../types';

function cacheKey(restaurantId: string) {
  return `menu_cache_${restaurantId}`;
}

function saveCache(restaurantId: string, data: { categories: Category[]; menuItems: MenuItem[] }) {
  try { localStorage.setItem(cacheKey(restaurantId), JSON.stringify({ ...data, at: Date.now() })); } catch {}
}

function loadCache(restaurantId: string): { categories: Category[]; menuItems: MenuItem[] } | null {
  try {
    const raw = localStorage.getItem(cacheKey(restaurantId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function useMenu(restaurant: Restaurant | null) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!restaurant) return;

    const now = new Date();
    const from = restaurant.active_from ? new Date(restaurant.active_from) : null;
    const until = restaurant.active_until ? new Date(restaurant.active_until) : null;

    // Trial/subscription expired but restaurant is still marked active → "coming soon" page
    if (restaurant.is_active && until && until < now) {
      setExpired(true);
      setLoading(false);
      return;
    }

    // Manually deactivated or not yet started → generic offline
    if (!restaurant.is_active) {
      setError(`Restoranti "${restaurant.name}" është jashtë shërbimit.`);
      setLoading(false);
      return;
    }
    if (from && from > now) {
      setError(`Restoranti "${restaurant.name}" është jashtë shërbimit.`);
      setLoading(false);
      return;
    }

    // Business-analytics view count for SuperAdmin's QR-menu-effectiveness metric — fire-and-forget,
    // once per browser tab per restaurant so repeat navigation within a visit isn't over-counted.
    // `source` lets us tell organic search / social / QR-or-direct apart, so SEO work is measurable.
    const viewedKey = `menu_viewed_${restaurant.id}`;
    if (!sessionStorage.getItem(viewedKey)) {
      sessionStorage.setItem(viewedKey, '1');
      let source = 'direct';
      let referrer_host: string | null = null;
      try {
        const ref = document.referrer || '';
        if (ref) {
          const u = new URL(ref);
          referrer_host = u.hostname.replace(/^www\./, '');
          if (u.hostname === window.location.hostname) source = 'internal';
          else if (/(^|\.)(google|bing|yahoo|duckduckgo|yandex|baidu|ecosia|brave)\./i.test(u.hostname)) source = 'search';
          else if (/(facebook|instagram|fb\.com|t\.co|twitter|x\.com|tiktok|linkedin|reddit|pinterest|wa\.me|whatsapp|telegram|messenger)/i.test(u.hostname)) source = 'social';
          else source = 'referral';
        }
      } catch { /* opaque referrer — leave as 'direct' */ }
      supabase.from('menu_views').insert({ restaurant_id: restaurant.id, source, referrer_host }).then(() => {}, () => {});
    }

    // Chain branches can share one restaurant's menu/prices instead of keeping their own.
    const menuRid = restaurant!.menu_source_id || restaurant!.id;

    async function fetchData() {
      setLoading(true);
      setOffline(false);
      setError(null);
      try {
        const [catRes, itemRes] = await Promise.all([
          supabase.from('categories').select('*').eq('restaurant_id', menuRid).order('sort_order'),
          supabase.from('menu_items').select('*').eq('restaurant_id', menuRid).eq('is_available', true).order('sort_order'),
        ]);

        if (catRes.error || itemRes.error) throw catRes.error || itemRes.error;

        const cats  = catRes.data  || [];
        const items = itemRes.data || [];
        setCategories(cats);
        setMenuItems(items);
        saveCache(menuRid, { categories: cats, menuItems: items });
      } catch (err: any) {
        const cached = loadCache(menuRid);
        if (cached) {
          setCategories(cached.categories);
          setMenuItems(cached.menuItems);
          setOffline(true);
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.id, restaurant?.menu_source_id, restaurant?.is_active, restaurant?.active_from, restaurant?.active_until]);

  // like_count changes from other people never show up on their own — the menu
  // is only fetched once on mount. Silently re-sync just the counts (no loading
  // spinner, nothing else touched) whenever the tab regains focus, so someone
  // switching back after a friend liked something sees the real number without
  // needing a hard reload.
  useEffect(() => {
    if (!restaurant) return;
    const menuRid = restaurant.menu_source_id || restaurant.id;
    const refreshLikeCounts = async () => {
      const { data } = await supabase.from('menu_items').select('id, like_count').eq('restaurant_id', menuRid);
      if (!data) return;
      const counts = new Map(data.map(d => [d.id, d.like_count]));
      setMenuItems(prev => prev.map(item => counts.has(item.id) ? { ...item, like_count: counts.get(item.id)! } : item));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refreshLikeCounts(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [restaurant?.id, restaurant?.menu_source_id]);

  return { categories, menuItems, setMenuItems, loading, error, offline, expired };
}
